import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4LandlordPropertyDamageArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Does Renters Insurance Cover Damage to Your Landlord\'s Property? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn when renters insurance liability coverage pays for accidental damage to your landlord\'s property, with examples like fire, water overflow, and common exclusions.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/does-renters-insurance-cover-damage-to-landlords-property"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Does renters insurance cover accidental damage to your landlord&apos;s property?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              This is one of the most important reasons to carry HO-4 — your personal liability coverage can help pay
              when you accidentally damage the rental unit or a neighbor&apos;s property.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Yes — renters insurance <strong>liability coverage</strong> often pays when you accidentally cause
                property damage to your landlord&apos;s building or another tenant&apos;s belongings. Examples include
                a kitchen fire, an overflowing bathtub, or a candle that damages walls. Your{' '}
                <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>liability limit</Link>{' '}
                determines how much protection you have.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common scenarios liability may cover</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Kitchen fire</strong> — smoke and fire damage to cabinets, walls, and flooring beyond normal wear.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Overflowing tub or sink</strong> — water damage to floors, ceilings below, or drywall. See also{' '}
                  <Link to="/knowledge-base/does-ho4-cover-water-damage" style={articleLinkStyle}>HO-4 water damage coverage</Link>.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Accidental hole in wall</strong> — beyond standard security-deposit wear and tear, if negligence is involved.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Damage spreading to a neighbor&apos;s unit</strong> — water or fire that affects adjacent apartments.
                </li>
                <li>Legal defense costs if the landlord or another tenant files a claim against you.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What liability typically does not cover</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Normal wear and tear</strong> — faded paint, carpet wear, minor nail holes from hanging pictures.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Intentional damage</strong> — vandalism or deliberate destruction is excluded.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Structural issues you did not cause</strong> — pre-existing mold, old plumbing failures, or building defects.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Damage from excluded perils</strong> — such as flood or earthquake unless you have separate coverage.
                </li>
                <li>
                  <strong>Business activities</strong> — home business property damage may need a separate endorsement.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Renters insurance vs. security deposit</h3>
              <p>
                Your security deposit covers minor damages and cleaning at move-out. Renters insurance liability kicks
                in for larger accidental losses — especially when damage exceeds your deposit or affects other units.
                Without liability coverage, you could owe thousands out of pocket or face a lawsuit.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Why landlords require renters insurance</h3>
              <p>
                Many landlords require HO-4 because their building insurance covers the structure, but they want tenants
                to carry liability coverage for tenant-caused damage. This is a key reason leases include minimum
                liability limits — read more in{' '}
                <Link to="/knowledge-base/is-ho4-required-by-landlord" style={articleLinkStyle}>is HO-4 required by my landlord</Link>.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What to do if you cause accidental damage</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Notify your landlord immediately and document the damage with photos.</li>
                <li style={{ marginBottom: '0.5rem' }}>Contact your renters insurance company to report a potential liability claim.</li>
                <li style={{ marginBottom: '0.5rem' }}>Do not admit fault beyond factual description — let the insurer investigate.</li>
                <li>Keep records of all communication. See{' '}
                  <Link to="/knowledge-base/how-to-file-a-renters-insurance-claim" style={articleLinkStyle}>how to file a renters insurance claim</Link>.
                </li>
              </ol>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Protect yourself from liability if you accidentally damage your rental or a neighbor&apos;s unit.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Worried about liability exposure?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can help you choose a liability limit that protects against landlord damage claims.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4LandlordPropertyDamageArticlePage;
